/**
 * Accès à la couche « Canal ».
 *
 * Pour l'instant, SEUL le compte propriétaire voit le switch de canal et les
 * canaux autres que Telegram. Source de vérité unique ci-dessous.
 *
 * ⚠️ Gate FRONT (cache l'UI). Le vrai cadenas viendra côté backend (Phase 2) :
 * les routes `/api/channels/twitter/*` devront ré-imposer ce contrôle.
 */
import { useHasApprovedAppAccess } from "@/apps/useAppAccess";
import { useSession } from "@/core/auth/useSession";

/** Le seul compte PROPRIÉTAIRE des canaux (owner/approbateur). */
export const CHANNELS_OWNER_EMAIL = "http.bratz@gmail.com";

export function useCanUseChannels(): boolean {
  const { user } = useSession();
  return user?.email?.trim().toLowerCase() === CHANNELS_OWNER_EMAIL;
}

/**
 * Peut utiliser l'app **Twitter** : le propriétaire OU toute personne à qui il
 * a accordé l'accès (table `appAccess`, statut `approved`). C'est le gate à
 * consommer pour l'app/le panneau Twitter — `useCanUseChannels` reste le gate
 * « propriétaire » strict (switch de canal in-creator, achats marketplace…).
 *
 * ⚠️ Gate front (affichage). Le verrou des routes backend Twitter (Phase 2)
 * reste séparé.
 */
export function useCanUseTwitter(): boolean {
  const isOwner = useCanUseChannels();
  const granted = useHasApprovedAppAccess("twitter");
  return isOwner || granted;
}

/**
 * Peut utiliser l'app **Twitter — Sites** : le propriétaire OU toute personne à
 * qui il a accordé l'accès à `twitter-sites` (table `appAccess`).
 *
 * ⛔ Volontairement SANS lien avec `useCanUseTwitter`. Accorder l'app des sites
 * ne donne pas celle des créatrices, et réciproquement : c'est ce qui permet
 * d'ouvrir cumgram.com à un collaborateur externe sans lui montrer la table des
 * comptes X de Kayla. Seul le propriétaire a les deux, parce qu'il les possède.
 */
export function useCanUseTwitterSites(): boolean {
  const isOwner = useCanUseChannels();
  const granted = useHasApprovedAppAccess("twitter-sites");
  return isOwner || granted;
}

/**
 * Peut utiliser l'app **Telegram — Sites** : le propriétaire OU toute personne
 * à qui il a accordé `telegram-sites` (table `appAccess`).
 *
 * ⛔ Sans lien avec `useCanUseTelegramSites` ↔ le canal Telegram des
 * créatrices, qui n'a PAS de capability propre (il est dans
 * `BASE_CAPABILITIES`, toute org l'a). Ce sont deux parcs, et accorder celui
 * des sites ne doit rien ouvrir sur celui de Kayla.
 */
export function useCanUseTelegramSites(): boolean {
  const isOwner = useCanUseChannels();
  const granted = useHasApprovedAppAccess("telegram-sites");
  return isOwner || granted;
}

/**
 * Réservé au SUPERADMIN — gate de l'achat de comptes (marketplace). Acheter
 * débite un solde réel, donc c'est plus strict que `useCanUseChannels` : même
 * source que le backend (`session.is_superadmin`), qui ré-impose le contrôle.
 */
export function useIsSuperadmin(): boolean {
  const { user } = useSession();
  return user?.isSuperadmin === true;
}
